import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { EstadoPostulacion, Postulacion } from '../../../../core/models/postulaciones.models';
import { PostulacionesService } from '../../../../core/services/postulaciones.service';
import { etiquetaEstadoPostulacion } from '../../../../core/utils/postulaciones-filter.util';
import { EmptyFilterState } from '../../../../shared/components/empty-filter-state/empty-filter-state';
import { TablePagination } from '../../../../shared/components/table-pagination/table-pagination';

type OrdenCampo = 'id_postulacion' | 'concurso' | 'subdireccion' | 'folio' | 'proyecto' | 'estado' | 'avance';
type OrdenDireccion = 'asc' | 'desc';

@Component({
  selector: 'app-postulaciones-panel',
  imports: [CommonModule, FormsModule, RouterLink, EmptyFilterState, TablePagination],
  templateUrl: './postulaciones-panel.html',
  styleUrl: './postulaciones-panel.css',
})
export class PostulacionesPanel implements OnChanges {
  @Input({ required: true }) rol: RolPlataforma = 'analista';

  postulaciones: Postulacion[] = [];
  postulacionesOrdenadas: Postulacion[] = [];
  mensaje = '';
  idPostulacionSeleccionado = 'Todos';
  subdireccionSeleccionada = 'Todas';
  estadoSeleccionado = 'Todos';
  idsPostulacion: string[] = [];
  subdirecciones: string[] = [];
  estados: EstadoPostulacion[] = [];
  readonly opcionesCantidad = [10, 20, 30, 40, 50];
  cantidadPorPagina = 10;
  paginaActual = 1;

  ordenarPor: OrdenCampo = 'avance';
  ordenDireccion: OrdenDireccion = 'desc';

  constructor(private readonly postulacionesService: PostulacionesService) {
    this.cargar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Siempre recargamos cuando cambia el rol de entrada para evitar mostrar datos/mensaje del rol por defecto.
    if (changes['rol']) this.cargar();
  }

  aplicarOrden(): void {
    const factor = this.ordenDireccion === 'asc' ? 1 : -1;
    const filtradas = this.postulaciones.filter((item) => {
      const matchIdPostulacion = this.idPostulacionSeleccionado === 'Todos' || item.idPostulacion === this.idPostulacionSeleccionado;
      const matchSubdireccion = this.subdireccionSeleccionada === 'Todas' || item.subdireccion === this.subdireccionSeleccionada;
      const matchEstado = this.estadoSeleccionado === 'Todos' || item.estadoPostulacion === this.estadoSeleccionado;
      return matchIdPostulacion && matchSubdireccion && matchEstado;
    });

    this.postulacionesOrdenadas = [...filtradas].sort((a, b) => factor * this.comparar(a, b));
    this.paginaActual = 1;
  }

  get postulacionesPaginadas(): Postulacion[] {
    const paginaTotal = this.totalPaginas;
    if (this.paginaActual > paginaTotal) {
      this.paginaActual = paginaTotal;
    }

    const inicio = (this.paginaActual - 1) * this.cantidadPorPagina;
    return this.postulacionesOrdenadas.slice(inicio, inicio + this.cantidadPorPagina);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.postulacionesOrdenadas.length / this.cantidadPorPagina));
  }

  cambiarPagina(pagina: number): void {
    this.paginaActual = pagina;
  }

  cambiarCantidadDesdePaginador(cantidad: number): void {
    this.cantidadPorPagina = cantidad;
    this.paginaActual = 1;
  }

  private cargar(): void {
    this.postulacionesService.getMisPostulaciones().subscribe((data) => {
      if (this.rol === 'urc') {
        this.postulaciones = data;
      } else if (this.rol === 'analista') {
        this.postulaciones = data.filter((item) => item.asignadaAnalista);
      } else {
        this.postulaciones = [];
      }

      this.idsPostulacion = [...new Set(this.postulaciones.map((item) => item.idPostulacion))];
      this.subdirecciones = [...new Set(this.postulaciones.map((item) => item.subdireccion))];
      this.estados = [...new Set(this.postulaciones.map((item) => item.estadoPostulacion))];

      this.mensaje =
        this.rol === 'analista'
          ? 'Visualizas solo postulaciones asignadas.'
          : this.rol === 'urc'
            ? 'Visualizas postulaciones de todas las subdirecciones.'
            : 'Este módulo está disponible para analista y URC.';

      this.aplicarOrden();
    });
  }

  etiquetaEstado(estado: EstadoPostulacion): string {
    return etiquetaEstadoPostulacion(estado);
  }

  private comparar(a: Postulacion, b: Postulacion): number {
    if (this.ordenarPor === 'id_postulacion') return a.idPostulacion.localeCompare(b.idPostulacion);
    if (this.ordenarPor === 'concurso') return a.concurso.localeCompare(b.concurso);
    if (this.ordenarPor === 'subdireccion') return a.subdireccion.localeCompare(b.subdireccion);
    if (this.ordenarPor === 'folio') return a.folio.localeCompare(b.folio);
    if (this.ordenarPor === 'proyecto') return a.nombreProyecto.localeCompare(b.nombreProyecto);
    if (this.ordenarPor === 'estado') return this.etiquetaEstado(a.estadoPostulacion).localeCompare(this.etiquetaEstado(b.estadoPostulacion));
    return (a.avancePorcentaje ?? 0) - (b.avancePorcentaje ?? 0);
  }
}
